import { useEffect, useMemo, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Globe, Search, ExternalLink, CheckCircle2, X, Trash2, Download, Loader2, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import SavedSearchesBar from "./SavedSearchesBar";

const PROP_TYPES = [
  "", "Office", "Retail", "Industrial", "Multi-Family",
  "Hospitality", "Land", "Specialty", "Health Care",
];

const money = (n) => (Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

/**
 * Pull from LoopNet — search dialog + staged-results review panel.
 *
 * Workflow:
 *   1. User picks City/State + sale/lease + property type + limit
 *   2. POST /api/loopnet/search → results land in `comps_staged`
 *   3. Each row shows in the panel; user picks rows to import → POST /api/loopnet/staged/import
 *   4. Reject button hits DELETE /api/loopnet/staged/{id}
 *
 * Backend gracefully maps 429/5xx/auth errors into toast messages.
 */
export default function LoopNetPullDialog({ open, onOpenChange, onImported }) {
  const [configured, setConfigured] = useState(true);
  const [city, setCity] = useState("");
  const [saleLease, setSaleLease] = useState("sale");
  const [propertyType, setPropertyType] = useState("");
  const [limit, setLimit] = useState(10);
  const [searching, setSearching] = useState(false);
  const [staged, setStaged] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [importing, setImporting] = useState(false);
  const cityRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    api.get("/loopnet/status").then((r) => setConfigured(!!r.data.configured)).catch(() => setConfigured(false));
    refreshStaged();
    setTimeout(() => cityRef.current?.focus(), 150);
  }, [open]);

  const refreshStaged = async () => {
    try {
      const r = await api.get("/loopnet/staged");
      setStaged(r.data.items || []);
      setSelected(new Set((r.data.items || []).map((i) => i.id)));  // default-select all new
    } catch {
      setStaged([]);
    }
  };

  const search = async (e) => {
    e?.preventDefault();
    if (!city.trim()) {
      toast.error("Enter a city — e.g., Charlotte, NC");
      cityRef.current?.focus();
      return;
    }
    setSearching(true);
    try {
      const r = await api.post("/loopnet/search", {
        city: city.trim(),
        sale_or_lease: saleLease,
        property_type: propertyType || null,
        limit: Math.min(Math.max(Number(limit) || 10, 1), 100),
      });
      const newCount = r.data.count || 0;
      if (newCount === 0) toast.info(r.data.message || "No results");
      else toast.success(`Found ${newCount} listing${newCount === 1 ? "" : "s"} — review below`);
      await refreshStaged();
    } catch (err) {
      const status = err.response?.status;
      const detail = formatApiError(err.response?.data?.detail);
      if (status === 429) toast.error(detail || "Rate limited — try again in a minute");
      else if (status === 503) toast.error("LoopNet not configured (RAPIDAPI_KEY missing)");
      else if (status === 502) toast.error(detail || "LoopNet upstream error");
      else toast.error(detail || "Search failed");
    } finally {
      setSearching(false);
    }
  };

  const toggleRow = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    if (selected.size === staged.length) setSelected(new Set());
    else setSelected(new Set(staged.map((i) => i.id)));
  };

  const importSelected = async () => {
    if (!selected.size) {
      toast.error("Pick at least one row");
      return;
    }
    setImporting(true);
    try {
      const r = await api.post("/loopnet/staged/import", { ids: Array.from(selected) });
      toast.success(`Imported ${r.data.imported} comp${r.data.imported === 1 ? "" : "s"}`);
      await refreshStaged();
      onImported?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const reject = async (id) => {
    try {
      await api.delete(`/loopnet/staged/${id}`);
      setStaged((prev) => prev.filter((i) => i.id !== id));
      setSelected((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not reject");
    }
  };

  const clearAll = async () => {
    if (!staged.length) return;
    if (!window.confirm(`Discard all ${staged.length} staged listings?`)) return;
    try {
      await api.post("/loopnet/staged/clear");
      setStaged([]);
      setSelected(new Set());
      toast.success("Staging cleared");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not clear");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto" data-testid="loopnet-pull-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit" }} className="flex items-center gap-2">
            <Globe className="w-5 h-5 text-amber-600" /> Pull from LoopNet
            <span className="ml-2 px-2 py-0.5 rounded-sm bg-amber-50 border border-amber-200 text-amber-900 text-[10px] uppercase tracking-widest font-mono">unofficial · via RapidAPI</span>
          </DialogTitle>
        </DialogHeader>

        {!configured && (
          <div className="text-xs bg-rose-50 border border-rose-200 text-rose-900 rounded-sm px-3 py-2">
            <b>Not configured.</b> Set <code>RAPIDAPI_KEY</code> in <code>backend/.env</code> and restart the backend.
          </div>
        )}

        <SavedSearchesBar
          source="loopnet"
          currentParams={{
            city: city.trim(),
            sale_or_lease: saleLease,
            property_type: propertyType || null,
            limit: Math.min(Math.max(Number(limit) || 10, 1), 100),
          }}
          canSave={!!city.trim()}
          onApply={(p) => {
            setCity(p.city || "");
            setSaleLease(p.sale_or_lease || "sale");
            setPropertyType(p.property_type || "");
            setLimit(p.limit || 10);
          }}
          onAfterRun={async () => { await refreshStaged(); }}
        />

        <form onSubmit={search} className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end" data-testid="loopnet-search-form">
          <label className="md:col-span-5">
            <div className="overline text-slate-600 mb-1">City, State</div>
            <input
              ref={cityRef}
              data-testid="loopnet-city-input"
              className="input-clean w-full"
              placeholder="Charlotte, NC"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              required
            />
          </label>
          <label className="md:col-span-2">
            <div className="overline text-slate-600 mb-1">Sale / Lease</div>
            <select className="input-clean w-full" data-testid="loopnet-sale-lease" value={saleLease} onChange={(e) => setSaleLease(e.target.value)}>
              <option value="sale">For Sale</option>
              <option value="lease">For Lease</option>
            </select>
          </label>
          <label className="md:col-span-2">
            <div className="overline text-slate-600 mb-1">Property type</div>
            <select className="input-clean w-full" data-testid="loopnet-property-type" value={propertyType} onChange={(e) => setPropertyType(e.target.value)}>
              {PROP_TYPES.map((p) => <option key={p || "any"} value={p}>{p || "Any"}</option>)}
            </select>
          </label>
          <label className="md:col-span-1">
            <div className="overline text-slate-600 mb-1">Count</div>
            <input
              type="number"
              min="1"
              max="100"
              className="input-clean w-full"
              data-testid="loopnet-limit"
              value={limit}
              onChange={(e) => setLimit(e.target.value)}
            />
          </label>
          <button
            type="submit"
            disabled={searching || !configured}
            className="btn-primary-dark md:col-span-2 inline-flex items-center justify-center gap-2 disabled:opacity-60 h-10"
            data-testid="loopnet-search-btn"
          >
            {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            {searching ? "Searching…" : "Search"}
          </button>
        </form>

        {/* Staged results */}
        <div className="mt-5">
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <div className="overline text-slate-600">Staged for review {staged.length > 0 && <span className="text-slate-900">({staged.length})</span>}</div>
            {staged.length > 0 && (
              <div className="flex items-center gap-2">
                <button onClick={toggleAll} type="button" className="text-xs text-slate-700 hover:text-slate-900 font-mono uppercase tracking-widest">
                  {selected.size === staged.length ? "Deselect all" : "Select all"}
                </button>
                <button onClick={clearAll} type="button" className="text-xs text-red-600 hover:text-red-800 font-mono uppercase tracking-widest" data-testid="loopnet-clear-all">
                  Clear
                </button>
              </div>
            )}
          </div>

          {staged.length === 0 ? (
            <div className="text-sm text-slate-400 text-center py-8 border border-dashed border-slate-200 rounded-sm">
              No staged listings yet. Run a search above to populate this panel.
            </div>
          ) : (
            <div className="border border-slate-200 rounded-sm overflow-hidden max-h-[44vh] overflow-y-auto">
              <table className="w-full text-sm" data-testid="loopnet-staged-table">
                <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
                  <tr className="text-left">
                    <th className="px-3 py-2 w-10"></th>
                    <th className="px-3 py-2 w-20">Photo</th>
                    <th className="px-3 py-2 overline text-slate-600">Address</th>
                    <th className="px-3 py-2 overline text-slate-600">Type</th>
                    <th className="px-3 py-2 overline text-slate-600 text-right">Sqft</th>
                    <th className="px-3 py-2 overline text-slate-600 text-right">Price</th>
                    <th className="px-3 py-2 overline text-slate-600 text-right">Cap</th>
                    <th className="px-3 py-2 overline text-slate-600">Broker</th>
                    <th className="px-3 py-2 w-16 text-right"></th>
                  </tr>
                </thead>
                <tbody>
                  {staged.map((c) => {
                    const checked = selected.has(c.id);
                    return (
                      <tr
                        key={c.id}
                        onClick={() => toggleRow(c.id)}
                        className={`border-b border-slate-100 last:border-0 cursor-pointer ${checked ? "bg-emerald-50/50" : "hover:bg-slate-50"}`}
                        data-testid={`loopnet-staged-row-${c.id}`}
                      >
                        <td className="px-3 py-2">
                          <input type="checkbox" readOnly checked={checked} />
                        </td>
                        <td className="px-3 py-2">
                          {c.thumbnail_url ? (
                            <img src={c.thumbnail_url} alt="" className="w-14 h-10 object-cover rounded-sm border border-slate-200" loading="lazy" />
                          ) : (
                            <div className="w-14 h-10 bg-slate-100 border border-slate-200 rounded-sm flex items-center justify-center text-slate-300">
                              <ImageIcon className="w-4 h-4" />
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <div className="font-semibold text-slate-900 truncate max-w-xs" title={c.formatted_address || c.address}>
                            {c.formatted_address || c.address}
                          </div>
                          {c.submarket && <div className="text-[10px] text-slate-400">{c.submarket}</div>}
                        </td>
                        <td className="px-3 py-2 text-xs text-slate-600 capitalize">{(c.property_type || "other").replace(/_/g, " ")}</td>
                        <td className="px-3 py-2 text-right font-mono">{c.sqft ? c.sqft.toLocaleString() : "—"}</td>
                        <td className="px-3 py-2 text-right font-mono font-bold">{c.sale_price ? `$${money(c.sale_price)}` : (c.monthly_rent ? `$${money(c.monthly_rent)}/mo` : "On Request")}</td>
                        <td className="px-3 py-2 text-right font-mono">{c.cap_rate != null ? `${c.cap_rate.toFixed(2)}%` : "—"}</td>
                        <td className="px-3 py-2 text-xs text-slate-600">
                          <div className="truncate max-w-[140px]" title={c.broker || ""}>{c.broker || "—"}</div>
                          {c.broker_name && <div className="text-[10px] text-slate-400">{c.broker_name}</div>}
                        </td>
                        <td className="px-3 py-2 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                          {c.external_url && (
                            <a href={c.external_url} target="_blank" rel="noreferrer" className="inline-block text-slate-400 hover:text-slate-700 p-1" title="Open in LoopNet" data-testid={`loopnet-open-${c.id}`}>
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          )}
                          <button onClick={() => reject(c.id)} className="inline-block text-slate-400 hover:text-red-600 p-1" title="Reject" data-testid={`loopnet-reject-${c.id}`}>
                            <X className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <DialogFooter className="mt-4">
          <button type="button" onClick={() => onOpenChange(false)} className="btn-outline-dark">Close</button>
          <button
            type="button"
            onClick={importSelected}
            disabled={importing || !selected.size}
            className="btn-primary-dark inline-flex items-center gap-2 disabled:opacity-60"
            data-testid="loopnet-import-btn"
          >
            {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            Import {selected.size || ""} to library
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
